import { capitalize } from "lodash"
import { type CustomNaming } from "../server/api/routers/customNaming"
import { z } from "zod"

export const customNamingSchema = z.array(
  z.object({
    id: z.number(),
    systemName: z.string(),
    description: z.string().optional(),
    customName: z.string(),
    customNamePlural: z.string(),
  }),
)

export type SystemName =
  | "preorder"
  | "planner"
  | "sale"
  | "unfulfillable_preorder"
  | "at_risk_preorder"
  | "available_quantity"
const systemNameVariants: Map<SystemName, { singular: string[]; plural: string[] }> = new Map([
  [
    "preorder",
    {
      singular: ["preorder", "pre-order", "pre order"],
      plural: ["preorders", "pre-orders", "pre orders"],
    },
  ],
  ["planner", { singular: ["planner"], plural: ["planners"] }],
  ["sale", { singular: ["sale"], plural: ["sales"] }],
  [
    "unfulfillable_preorder",
    {
      singular: ["unfulfillable preorder"],
      plural: ["unfulfillable preorders"],
    },
  ],
  [
    "at_risk_preorder",
    {
      singular: ["at-risk preorder"],
      plural: ["at-risk preorders"],
    },
  ],
  [
    "available_quantity",
    {
      singular: ["available to sell"],
      plural: ["available to sell"],
    },
  ],
])

function getAndParseStorage(key: string, storage: "local" | "session") {
  const storageObject = storage === "local" ? globalThis.localStorage : globalThis.sessionStorage
  // Node 25 exposes a method-less `localStorage`/`sessionStorage` stub (no --localstorage-file),
  // so a truthy check isn't enough — feature-detect getItem before calling it.
  if (typeof storageObject?.getItem !== "function") return undefined
  const value = storageObject.getItem(key)
  if (!value || value === "undefined") return undefined
  try {
    return JSON.parse(value)
  } catch (e) {
    console.error(`Error parsing ${key} from ${storage}Storage:`, e)
    return undefined
  }
}

function formatSystemName(
  isCapitalized: boolean,
  isPlural: boolean,
  systemName: SystemName,
): string {
  const dSystemName = systemNameVariants.get(systemName)

  if (dSystemName) {
    const name = (isPlural ? dSystemName.plural[0] : dSystemName.singular[0]) ?? systemName
    return isCapitalized ? capitalize(name) : name
  }

  return isCapitalized ? capitalize(systemName) : systemName
}

export function formatName(
  systemName: SystemName,
  isPlural: boolean = false,
  isCapitalized: boolean = false,
) {
  // Handle toggling custom naming (hakio only)
  const showCustomNaming = getAndParseStorage("template-show-custom-naming", "session") ?? "false"
  if (!showCustomNaming) return formatSystemName(isCapitalized, isPlural, systemName)

  // Get orgId from localStorage
  const orgId = getAndParseStorage("orgId", "local")
  if (!orgId) return formatSystemName(isCapitalized, isPlural, systemName)

  // Get custom naming from localStorage
  const customNaming = getAndParseStorage(`${orgId}.custom-naming`, "local")
  if (!customNaming) return formatSystemName(isCapitalized, isPlural, systemName)

  const parsedCustomNaming = customNamingSchema.parse(customNaming) as CustomNaming[]
  const name = parsedCustomNaming.find(
    (n) => n.systemName.toLowerCase() === systemName.toLowerCase(),
  )

  if (name) {
    const stringToReturn = isPlural ? name.customNamePlural : name.customName
    return isCapitalized ? capitalize(stringToReturn) : stringToReturn
  }

  return formatSystemName(isCapitalized, isPlural, systemName)
}

// Resolve {token} placeholders (e.g. "{sales}", "{Preorder}") emitted by
// server-side code. Always applied — formatName() itself handles the
// custom-naming toggle, so this returns the default term when off and the
// custom term when on.
const resolvePlaceholders = (input: string): string =>
  input.replace(/\{([a-zA-Z]+)\}/g, (raw, token: string) => {
    const lowerToken = token.toLowerCase()
    const isCapitalized = token[0] === token[0]?.toUpperCase()
    for (const [systemName, variants] of systemNameVariants) {
      if (variants.singular.includes(lowerToken)) {
        return formatName(systemName, false, isCapitalized)
      }
      if (variants.plural.includes(lowerToken)) {
        return formatName(systemName, true, isCapitalized)
      }
    }
    return raw
  })

export const replaceName = (input: string): string => {
  const result = resolvePlaceholders(input)

  const showCustomNaming = getAndParseStorage("show-custom-naming", "session") ?? false
  if (!showCustomNaming) return result

  let withCustomWords = result
  for (const [key, variants] of systemNameVariants) {
    for (const variant of [...variants.singular, ...variants.plural]) {
      const regex = new RegExp(`\\b${variant}\\b`, "gi") // Global, case-insensitive
      withCustomWords = withCustomWords.replace(regex, (match) => {
        const isPlural = variants.plural.includes(variant.toLowerCase())
        let replacement = formatName(key, isPlural)

        // Preserve capitalization of the first letter
        if (match[0] === match[0]?.toUpperCase()) {
          replacement = capitalize(replacement)
        }

        return replacement
      })
    }
  }

  return withCustomWords
}

export const joinCollectionIds = (collectionIds: string[]) => collectionIds.join("-&-")

export const splitCollectionIdString = (collectionIdString: string) =>
  collectionIdString.split("-&-")
